import { PhysicalLinkPanel } from "../../components/physical/PhysicalLinkPanel"
import { EquipmentContextEditor } from "../../components/en1176/EquipmentContextEditor"
import { EquipmentContextQuestionnaire } from "../../components/en1176/EquipmentContextQuestionnaire"
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
} from "@ant-design/icons"
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Popconfirm,
  Row,
  Spin,
  Tag,
  Typography,
  message,
} from "antd"
import dayjs from "dayjs"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Link, Prompt, useHistory, useLocation } from "react-router-dom"
import { CatalogAssignment, TemplateSelector } from "../../components/en1176/CatalogAssignment"
import {
  useRegulatoryAnswers,
  useRegulatoryEntitySave,
  useRegulatoryEquipment,
  useRegulatorySite,
} from "../../components/en1176/useRegulatoryAnswers"
import { assignRevision, legacyControls, regulatoryUuid } from "../../utils/regulatoryPresentation"
import { useAssignedCatalog, useRegulatoryTemplates } from "../Regulatory/useOperationalCatalog"

import { FlexCol, FlexRow, UnderlinedSectionTitle } from "../../components/commons-ts/common"
import { EntityTimestamps } from "../../components/commons-ts/entityTimestamps"
import { Label } from "../../components/commons-ts/input"
import { SimplifiedBreadcrumb } from "../../components/commons-ts/simplifiedBreadcrumb"
import { UploadImageListV2 } from "../../components/commons-ts/uploadImageV2"
import { Equipment, StorageEntityType, UUID } from "../../generated/proto-ts/main"
import { Translated } from "../../utils/translated"
import {
  CENTRAL_CONTAINER_GAP,
  parsePathForEntityUUID,
  pbUUIDToUuid,
  uuidToPbUUID,
} from "../../utils/utils"

import { EN1176QuestionnaireWidget } from "../../components/en1176/EN1176QuestionnaireWidget"

import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { useTranslationContext } from "../../providers/translationProvider"
import { useUsercommDeleteEquipmentBimodal } from "../../usercomm/common/usercommAsyncRequestBimodal"

export const EN1176EquipmentPage: FC = () => {
  const [antdMessageCtx, contextHolder] = message.useMessage()
  const location = useLocation()
  const history = useHistory()
  const { translated } = useTranslationContext()
  const { currentEnterprise } = useSyncronizationContext()

  const memoEquipmentUUID = useMemo((): UUID | null => {
    let equipUUID = parsePathForEntityUUID(location.pathname)
    if (equipUUID === null) return null
    return uuidToPbUUID(equipUUID)
  }, [location.pathname])

  const memoEquipmentUUIDStr = useMemo(() => {
    return memoEquipmentUUID ? pbUUIDToUuid(memoEquipmentUUID) : null
  }, [memoEquipmentUUID])

  // Equipment Hooks
  const [equipment, getEquipment, externallySetEquipment, entityError] = useRegulatoryEquipment()
  const entitySave = useRegulatoryEntitySave()
  const [deleteEquipmentAck, deleteEquipment] = useUsercommDeleteEquipmentBimodal()

  // Site Getter
  const [site, getSite] = useRegulatorySite()

  const answerResource = useRegulatoryAnswers(
    memoEquipmentUUIDStr ?? "",
    StorageEntityType.EQUIPMENT,
  )
  const equipmentAnswers = answerResource.answers
  const templateResource = useRegulatoryTemplates()
  const regulatoryControlTemplates = templateResource.templates.filter((t) => t.Scope !== "site")
  const assignedUUID = equipment ? regulatoryUuid(equipment.regulatory_template_uuid) : ""
  const catalog = useAssignedCatalog(
    "equipment",
    equipment ? regulatoryUuid(equipment.uuid) : "",
    equipment
      ? `${assignedUUID}/${regulatoryUuid(equipment.regulatory_template_revision_uuid)}`
      : "",
  )
  const [configurationDirty, setConfigurationDirty] = useState(false)
  const [selectedRevision, setSelectedRevision] = useState<string | undefined>()
  const combined = !!catalog.data?.contextual?.configuration.enabled
  const regulatoryControlTemplate = catalog.template
  const templateControlPoints = catalog.points
  const [selectedRegulatoryTemplateUUID, setSelectedRegulatoryTemplateUUID] = useState<
    string | null
  >(null)

  // Equipment Local State
  const [siteUUIDStr, setSiteUUIDStr] = useState<string | null>(null)
  const [siteName, setSiteName] = useState<string | null>(null)
  const [equipmentName, setEquipmentName] = useState<string | null>(null)
  const [equipmentManufacturer, setEquipmentManufacturer] = useState<string | null>(null)
  const [equipmentRef, setEquipmentRef] = useState<string | null>(null)
  const [equipmentDate, setEquipmentDate] = useState<dayjs.Dayjs | null>(null)
  const [pictures, setPictures] = useState<UUID[] | null>(null)
  const [createdAt, setCreatedAt] = useState<number | null>(null)

  // Fetch Equipment
  useEffect(() => {
    if (memoEquipmentUUID === null) return
    getEquipment(memoEquipmentUUID)
  }, [memoEquipmentUUID])

  // Set Equipment State
  useEffect(() => {
    if (equipment === null) return
    setSelectedRevision(regulatoryUuid(equipment.regulatory_template_revision_uuid) || undefined)
    if (equipment.site_uuid) {
      setSiteUUIDStr(pbUUIDToUuid(equipment.site_uuid))
    }
    setEquipmentName(equipment.equipment_name)
    setEquipmentManufacturer(equipment.equipment_manufacturer)
    setEquipmentRef(equipment.equipment_reference)
    if (equipment.regulatory_template_uuid) {
      setSelectedRegulatoryTemplateUUID(regulatoryUuid(equipment.regulatory_template_uuid))
    } else {
      setSelectedRegulatoryTemplateUUID(null)
    }
    setPictures(equipment.pictures)
    setCreatedAt(equipment.created_at)

    if (equipment.equipment_installation_date === 0) {
      setEquipmentDate(null)
    } else {
      setEquipmentDate(dayjs(equipment.equipment_installation_date))
    }
  }, [equipment])

  // Fetch Parent Site
  useEffect(() => {
    if (siteUUIDStr === null) return
    getSite(uuidToPbUUID(siteUUIDStr))
  }, [siteUUIDStr])

  useEffect(() => {
    if (site === null) return
    setSiteName(site.site_name)
  }, [site])

  // Mutation check
  const memoEquipmentIsMutated = useMemo(() => {
    if (equipment === null || createdAt === null) return false

    const prevTmplStr = equipment.regulatory_template_uuid
      ? regulatoryUuid(equipment.regulatory_template_uuid)
      : null
    let instDateInt = equipmentDate ? equipmentDate.toDate().getTime() : 0

    let mutated =
      selectedRevision !==
        (regulatoryUuid(equipment.regulatory_template_revision_uuid) || undefined) ||
      equipment.equipment_name !== (equipmentName || "") ||
      equipment.equipment_manufacturer !== (equipmentManufacturer || "") ||
      equipment.equipment_reference !== (equipmentRef || "") ||
      prevTmplStr !== selectedRegulatoryTemplateUUID ||
      equipment.equipment_installation_date !== instDateInt

    if (pictures !== null) {
      const prevPics = equipment.pictures.map((u) => pbUUIDToUuid(u)).join(",")
      const currPics = pictures.map((u) => pbUUIDToUuid(u)).join(",")
      if (prevPics !== currPics) mutated = true
    }
    return mutated
  }, [
    equipment,
    equipmentName,
    equipmentManufacturer,
    equipmentRef,
    selectedRegulatoryTemplateUUID,
    selectedRevision,
    equipmentDate,
    pictures,
    createdAt,
  ])

  const onUpdateEquipment = async () => {
    if (equipment === null) return
    const updated = equipment.clone()
    updated.equipment_name = equipmentName ?? ""
    updated.equipment_manufacturer = equipmentManufacturer ?? ""
    updated.equipment_reference = equipmentRef ?? ""
    if (selectedRegulatoryTemplateUUID) {
      updated.regulatory_template_uuid = uuidToPbUUID(selectedRegulatoryTemplateUUID)
    }
    assignRevision(updated, selectedRegulatoryTemplateUUID !== assignedUUID, selectedRevision)
    if (equipmentDate) updated.equipment_installation_date = equipmentDate.toDate().getTime()
    if (pictures) updated.pictures = pictures
    updated.updated_at = Date.now()

    if (answerResource.dirty) {
      antdMessageCtx.warning("Attendez la confirmation des réponses avant de modifier la fiche.")
      return
    }
    try {
      const stored = await entitySave.saveEntity(
        updated,
        StorageEntityType.EQUIPMENT,
        updated.site_uuid,
      )
      externallySetEquipment(Equipment.deserializeBinary(stored))
      if (!configurationDirty) catalog.reload()
      antdMessageCtx.success("Fiche enregistrée.")
    } catch (reason) {
      antdMessageCtx.error(reason instanceof Error ? reason.message : String(reason))
    }
  }

  const onDeleteEquipment = useCallback(() => {
    if (memoEquipmentUUID === null) return
    deleteEquipment(memoEquipmentUUID)
  }, [memoEquipmentUUID])

  useEffect(() => {
    if (deleteEquipmentAck) {
      antdMessageCtx.info(translated("Équipement supprimé !"))
      if (siteUUIDStr) {
        history.push(`/regulatory/sites/${siteUUIDStr}`)
      } else {
        history.push("/")
      }
    }
  }, [deleteEquipmentAck, siteUUIDStr])

  if (entityError)
    return (
      <Alert
        type="error"
        showIcon
        message={entityError}
        action={<Button onClick={() => window.location.reload()}>Réessayer</Button>}
      />
    )

  if (equipment === null) {
    return (
      <FlexCol
        style={{ width: "100%", height: "60vh", justifyContent: "center", alignItems: "center" }}
      >
        <Spin size="large" />
      </FlexCol>
    )
  }

  return (
    <>
      <Prompt
        when={configurationDirty || answerResource.dirty}
        message="Des modifications de composants ou de réponses ne sont pas enregistrées. Quitter cette page ?"
      />
      {contextHolder}
      <FlexCol
        className="regulatory-operational"
        style={{ width: "100%", gap: CENTRAL_CONTAINER_GAP }}
      >
        <SimplifiedBreadcrumb
          root="/"
          previousItems={[
            { label: "Accueil", href: "/" },
            {
              label: siteName || "Site",
              href: siteUUIDStr ? `/regulatory/sites/${siteUUIDStr}` : "/",
            },
          ]}
          currentItem={{ label: equipmentName || "Équipement" }}
        />
        <PhysicalLinkPanel
          id={memoEquipmentUUIDStr ?? ""}
          equipment
          refreshKey={catalog.data?.contextual?.configuration.version}
          disabled={configurationDirty}
          onChanged={catalog.reload}
        />

        {/* Back Link & Title */}
        <Row justify="space-between" align="middle" style={{ marginTop: "0.5rem" }}>
          <Col>
            <FlexCol style={{ gap: 4 }}>
              <Link to={siteUUIDStr ? `/regulatory/sites/${siteUUIDStr}` : "/"}>
                <Button icon={<ArrowLeftOutlined />} size="small">
                  <Translated keyEn="Retour au site" />
                </Button>
              </Link>
              <Typography.Text style={{ fontSize: "2rem", fontWeight: "bold" }}>
                {equipmentName}
              </Typography.Text>
            </FlexCol>
          </Col>
          <Col>
            <Tag
              color="cyan"
              style={{ fontSize: "1rem", padding: "6px 14px", borderRadius: "6px" }}
            >
              <SafetyCertificateOutlined />{" "}
              {combined
                ? "Équipement composé"
                : regulatoryControlTemplate
                  ? regulatoryControlTemplate.Name
                  : "Aucun modèle"}
            </Tag>
          </Col>
        </Row>

        {/* Equipment Identification Card */}
        <Card
          size="small"
          title={<Translated keyEn="Identification & Caractéristiques de l'équipement" />}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Label>
                <Translated keyEn="Nom de l'équipement" />
              </Label>
              <Input
                size="large"
                value={equipmentName ?? ""}
                onChange={(e) => setEquipmentName(e.target.value)}
              />
            </Col>
            <Col xs={24} md={12}>
              <Label>
                <Translated keyEn="Modèle d'inspection appliqué (Norme)" />
              </Label>
              {combined ? (
                <>
                  <Typography.Paragraph>
                    Les révisions sont affectées à chaque composant.
                  </Typography.Paragraph>
                  <a href="#equipment-configuration">Consulter les composants et leurs modèles</a>
                </>
              ) : (
                <TemplateSelector
                  value={selectedRegulatoryTemplateUUID}
                  onChange={(value) => {
                    setSelectedRegulatoryTemplateUUID(value)
                    setSelectedRevision(undefined)
                  }}
                  disabled={
                    combined ||
                    configurationDirty ||
                    catalog.loading ||
                    !!catalog.error ||
                    answerResource.dirty ||
                    entitySave.saving
                  }
                />
              )}
            </Col>
            <Col xs={24} md={8}>
              <Label>
                <Translated keyEn="Fabricant" />
              </Label>
              <Input
                size="large"
                value={equipmentManufacturer ?? ""}
                onChange={(e) => setEquipmentManufacturer(e.target.value)}
                placeholder="Ex: HAGS, Husson, Proludic..."
              />
            </Col>
            <Col xs={24} md={8}>
              <Label>
                <Translated keyEn="Référence / N° de série" />
              </Label>
              <Input
                size="large"
                value={equipmentRef ?? ""}
                onChange={(e) => setEquipmentRef(e.target.value)}
                placeholder="Référence fabricant"
              />
            </Col>
            <Col xs={24} md={8}>
              <Label>
                <Translated keyEn="Date d'installation" />
              </Label>
              <DatePicker
                size="large"
                style={{ width: "100%" }}
                value={equipmentDate}
                onChange={(val) => setEquipmentDate(val)}
              />
            </Col>
            <Col xs={24}>
              <Label>
                <Translated keyEn="Photos de l'équipement" />
              </Label>
              {pictures !== null && (
                <UploadImageListV2 pictures={pictures} setPictures={setPictures} />
              )}
            </Col>
          </Row>
        </Card>

        {/* Interactive Control Points Questionnaire */}
        <UnderlinedSectionTitle>
          <Translated keyEn="Points de contrôle d'inspection (Norme EN 1176)" />
        </UnderlinedSectionTitle>

        {!combined && (
          <>
            {" "}
            <CatalogAssignment
              catalog={catalog}
              assigned={assignedUUID}
              selected={selectedRegulatoryTemplateUUID}
              onChange={(value) => {
                setSelectedRegulatoryTemplateUUID(value)
                setSelectedRevision(undefined)
              }}
              selectedRevision={selectedRevision}
              onRevisionChange={setSelectedRevision}
              scope="equipment"
              disabled={
                combined ||
                configurationDirty ||
                catalog.loading ||
                !!catalog.error ||
                answerResource.dirty ||
                entitySave.saving
              }
            />
          </>
        )}
        {catalog.error && (
          <Alert
            type="error"
            message={catalog.error.message}
            action={<Button onClick={catalog.reload}>Réessayer</Button>}
          />
        )}
        {catalog.data?.contextual && (
          <EquipmentContextEditor
            key={memoEquipmentUUIDStr!}
            data={catalog.data.contextual}
            guardNavigation={false}
            onDirtyChange={setConfigurationDirty}
            blocked={answerResource.dirty || entitySave.saving || memoEquipmentIsMutated}
            onPublished={() => {
              catalog.reload()
              answerResource.reload()
            }}
          />
        )}
        {catalog.data?.contextual ? (
          <EquipmentContextQuestionnaire
            data={catalog.data.contextual}
            points={catalog.points}
            answers={answerResource}
            readOnly={!!catalog.error || catalog.loading}
          />
        ) : (
          <>
            {" "}
            <EN1176QuestionnaireWidget
              controlPoints={
                catalog.data?.resolved ? templateControlPoints : legacyControls(equipmentAnswers)
              }
              saveStates={answerResource.states}
              retry={answerResource.retry}
              loading={answerResource.loading || catalog.loading}
              error={answerResource.error}
              reload={answerResource.reload}
              connected={answerResource.connected}
              readOnly={!catalog.data?.resolved || !!catalog.error}
              answers={equipmentAnswers}
              onAnswersChange={answerResource.save}
              onAnswerBlur={answerResource.flush}
              title={
                regulatoryControlTemplate ? (
                  regulatoryControlTemplate.Name
                ) : (
                  <Translated keyEn="Aucun modèle sélectionné" />
                )
              }
            />
          </>
        )}

        {/* Save & Delete Toolbar */}
        <FlexRow
          style={{
            justifyContent: "flex-end",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            marginTop: 20,
          }}
        >
          <Popconfirm
            title={<Translated keyEn="Êtes-vous sûr de vouloir supprimer cet équipement ?" />}
            onConfirm={onDeleteEquipment}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              <Translated keyEn="Supprimer l'équipement" />
            </Button>
          </Popconfirm>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            disabled={!memoEquipmentIsMutated || entitySave.saving || answerResource.dirty}
            loading={entitySave.saving}
            onClick={onUpdateEquipment}
          >
            <Translated keyEn="Enregistrer" />
          </Button>
        </FlexRow>

        <EntityTimestamps entity={equipment} />
      </FlexCol>
    </>
  )
}
